<template>
  <div
    :style="position"
    tabindex="0"
    ref="contextMenu"
    class="context-menu"
    @focusin="focusIn"
    @focusout="focusOut"
    :class="'context-menu--' + flowDirection"
  >
    <slot />
  </div>
</template>

<script>
import { each, filter } from 'lodash'

export default {
  props: {
    x: {
      type: Number,
      default: null
    },

    y: {
      type: Number,
      default: null
    },

    width: {
      type: Number,
      default: 223
    },

    childContextMenu: {
      type: Boolean,
      default: false
    },

    passedFlowDirection: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      preventBlur: false,
      childHeight: 25 // The single child height in pixels
    }
  },

  mounted () {
    // When the div is mounted, set the focus.
    this.setFocus()
  },

  computed: {
    // Amount of children that aren't a comment (which happens when the v-if condition for a component is false)
    amountOfChildren () {
      return filter(this.$slots.default, vnode => !vnode.isComment).length
    },

    // The expected height of the container
    height () {
      return this.amountOfChildren * this.childHeight + 2
    },

    // The flow direction of the child context menus
    flowDirection () {
      if (!this.childContextMenu) {
        const result = this.figureOutDeepestChild(this.$vnode)

        // If it doesn't fit, default to the right
        if (
          this.x + result * this.width > window.innerWidth &&
          this.x - result * this.width > 0
        ) {
          return 'left'
        }
      } else {
        // The flow direction fo the child context menus is only decided by the top Context Menu
        return this.passedFlowDirection
      }

      return 'right'
    },

    // The final position of the Context Menu
    position () {
      const result = {
        'width': this.width + 'px',
        'left': (this.x + 5) + 'px',
        'top': this.y + 'px'
      }

      if (this.passedFlowDirection === 'left') {
        result.left = this.x * -1 - 2 + -3 + 'px'
      } else {
        if (
          this.x + this.width > window.innerWidth &&
          this.x + (this.width / 2) <= window.innerWidth
        ) {
          result.left = window.innerWidth - this.width + 'px'
        } else if (
          this.x + (this.width / 2) > window.innerWidth
        ) {
          result.left = this.x - this.width + 'px'
        }

        if (
          this.y + this.height > window.innerHeight &&
          this.y + (this.height / 2) <= window.innerHeight
        ) {
          result.top = window.innerHeight - this.height + 'px'
        } else if (
          this.y + (this.height / 2) > window.innerHeight
        ) {
          result.top = this.y - this.height + 'px'
        }
      }

      return result
    }
  },

  methods: {
    // Recursive function to figure out the amount of child context menu items
    figureOutDeepestChild (vnode) {
      let highest = 0
      const children = vnode.componentOptions ? vnode.componentOptions.children : undefined

      if (!children) {
        return 0
      }

      each(children, child => {
        highest = Math.max(highest, this.figureOutDeepestChild(child))
      })

      return highest + 1
    },

    // Actions to set focus and lose focus
    focusIn () {
      this.preventBlur = true
    },

    focusOut () {
      const self = this

      this.preventBlur = false

      // We use setTimeout instead of Vue.$nextTick because we need to set a macro-event (task) instead of a micro-event (job).
      setTimeout(() => {
        if (!self.preventBlur) {
          self.$emit('blur')
        }
      })
    },

    setFocus () {
      const self = this

      this.$nextTick(() => {
        self.$refs.contextMenu.focus()
      })
    }
  }
}
</script>

<style>
  @media not all and (min-resolution: .001dpcm) {
    @media {

      .context-menu__item--has-children--chevron {
        margin-top: 5px!important;
        margin-right: -5px!important;
      }
    }
  }
</style>

<style lang="scss" scoped>
  @import '../../styling/styling.scss';
  @import '../../styling/definitions.scss';

  .context-menu {
    @extend %shadow;
    position: absolute;
    border-radius: rem(2);
    background-color: $white;

    /deep/
    &__item {
      height: rem(25);
      font-weight: 400;
      padding: 0 rem(10);
      font-size: rem(13);
      line-height: rem(25);
      color: $medium-grey-font;

      &:hover {
        cursor: pointer;
        color: $dark-grey-font;
        background-color: $light-grey-background;

        &:first-child {
          border-top-left-radius: rem(2);
          border-top-right-radius: rem(2);
        }

        &:last-child {
          border-bottom-left-radius: rem(2);
          border-bottom-right-radius: rem(2);
        }
      }

      &--icon {
        width: rem(16);
        height: rem(16);
        float: left;
        text-align: center;
        font-size: rem(16);
        margin-right: rem(10);
      }

      &--has-divider {
        height: rem(24);
        border-bottom: 1px solid $primary-border-color;
      }

      &--has-children {

        > .context-menu {

          &:before {
            width: 5px;
            height: 100%;
            left: -5px;
            content: '';
            position: absolute;
            background-color: transparent;
          }
        }

        &--chevron {
          width: rem(7);
          height: rem(7);
          float: right;
          font-size: rem(7);
          margin-top: rem(5);
          margin-right: rem(-7);
        }
      }

      &--no-icon { padding-left: rem(36); }
    }

    &--left {

      .context-menu__item--has-children {

        > .context-menu {

          &:before {
            display: none;
            visibility: hidden;
          }

          &:after {
            width: 5px;
            height: 100%;
            top: 0;
            right: -5px;
            content: '';
            position: absolute;
            background-color: transparent;
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// node_modules/pro4all-ui/ui-kit/components/ContextMenu/ContextMenu.vue